/**
 * Branded ID types. Each id-bearing entity gets a nominal type so we
 * can't accidentally pass a `SiteId` where a `PageId` is expected. The
 * encoded form is always plain UUID string; the branded type only
 * exists at the type level.
 */
import * as Schema from "effect/Schema";

/**
 * Ids INSIDE a published document are authored by the EDITOR, not the
 * database: "n1-welcome", "home-seed0-w0", "__header-logo". They are opaque
 * and stable, and they are not UUIDs — only `SiteId` is a database key. The
 * shared header/footer widgets are ids like `__header-logo`, so a LEADING
 * underscore is legal: a stricter first-character rule silently rejected real
 * documents (caught by the round-trip test, not by review).
 *
 * They were nonetheless declared as `Schema.UUID`, which made every real
 * document undecodable by its own schema. Rather than weaken the types, the
 * publish path simply stopped decoding at all (see serializeSiteDoc's header),
 * so ONE over-strict field disabled validation for the whole document. The
 * brand is what carries the value here — it prevents passing a PageId where a
 * WidgetId belongs — and it is kept; only the format is corrected to what the
 * domain actually produces. A UUID still satisfies this pattern, so documents
 * written before this change keep validating.
 */
const DocumentId = Schema.String.pipe(
  Schema.pattern(/^[A-Za-z0-9_][A-Za-z0-9._:-]{0,127}$/, {
    message: () =>
      "must be an opaque document id (max 128 chars of A-Z a-z 0-9 . _ : -)",
  }),
);

/** The DATABASE key for a site (the `sites` table primary key). Always a
 *  UUID — minted server-side. */
export const SiteId = Schema.UUID.pipe(Schema.brand("SiteId"));
export type SiteId = Schema.Schema.Type<typeof SiteId>;

/**
 * The site id a PUBLISHED DOCUMENT carries. Not the same thing as `SiteId`:
 * an account-free local draft publishes under its own `local-<uuid>` id, so
 * the document's id is not always a bare UUID. Conflating the two is why the
 * publish path could not decode its own output.
 */
export const DocumentSiteId = DocumentId.pipe(Schema.brand("DocumentSiteId"));
export type DocumentSiteId = Schema.Schema.Type<typeof DocumentSiteId>;

export const PageId = DocumentId.pipe(Schema.brand("PageId"));
export type PageId = Schema.Schema.Type<typeof PageId>;

export const EdgeId = DocumentId.pipe(Schema.brand("EdgeId"));
export type EdgeId = Schema.Schema.Type<typeof EdgeId>;

export const WidgetId = DocumentId.pipe(Schema.brand("WidgetId"));
export type WidgetId = Schema.Schema.Type<typeof WidgetId>;

/**
 * AssetSha256 is the sha256 hex digest of an asset's bytes — the asset
 * is stored at `assets/<sha>.<ext>` in garage and referenced by page
 * JSON. Content-addressed: same bytes → same id, dedup is free.
 */
export const AssetSha256 = Schema.String.pipe(
  Schema.pattern(/^[0-9a-f]{64}$/, {
    message: () => "must be a 64-char hex sha256",
  }),
  Schema.brand("AssetSha256"),
);
export type AssetSha256 = Schema.Schema.Type<typeof AssetSha256>;
